<template>
  <div class="map-wrapper">
    <div id="mapContainer"></div>
    
    <div class="sidebar">
      <div class="header">
        <h3>石家庄市老年人设施可达性分析</h3>
        <p class="subtitle">基于高斯两步移动搜索法</p>
      </div>
      
      <div class="stats-panel">
        <div class="stat-card">
          <div class="stat-icon"></div>
          <div class="stat-info">
            <div class="stat-value">{{ nursingHomes.length }}</div>
            <div class="stat-label">养老院</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon">📍</div>
          <div class="stat-info">
            <div class="stat-value">{{ streetData.length }}</div>
            <div class="stat-label">街道/乡镇</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon">👴</div>
          <div class="stat-info">
            <div class="stat-value">{{ (totalElderly65 / 10000).toFixed(2) }}万</div>
            <div class="stat-label">65岁以上人口</div>
          </div>
        </div>
      </div>
      
      <div class="controls">
        <h4 class="section-title">显示控制</h4>
        <div class="control-group">
          <label class="toggle-switch">
            <input type="checkbox" v-model="showNursingHomes" />
            <span class="slider"></span>
            <span class="label-text">养老院位置</span>
          </label>
        </div>
        <div class="control-group">
          <label class="toggle-switch">
            <input type="checkbox" v-model="showAccessibility" />
            <span class="slider"></span>
            <span class="label-text">可达性范围</span>
          </label>
        </div>
      </div>
      
      <div class="legend-panel">
        <h4 class="section-title">可达性图例</h4>
        <div class="legend-item">
          <span class="legend-color" style="background: #52c41a;"></span>
          <span class="legend-text">高可达性 (&gt;0.05)</span>
        </div>
        <div class="legend-item">
          <span class="legend-color" style="background: #faad14;"></span>
          <span class="legend-text">中可达性 (0.02-0.05)</span>
        </div>
        <div class="legend-item">
          <span class="legend-color" style="background: #f5222d;"></span>
          <span class="legend-text">低可达性 (&lt;0.02)</span>
        </div>
      </div>
      
      <div class="district-rank">
        <h4 class="section-title">区县可达性排名</h4>
        <div class="rank-list">
          <div class="rank-item" v-for="(d, i) in districtRank" :key="i" @click="focusOnDistrict(d.name)">
            <div class="rank-index" :class="{'top3': i < 3}">{{ i + 1 }}</div>
            <div class="rank-content">
              <div class="rank-name">{{ d.name }}</div>
              <div class="rank-bar">
                <div class="rank-bar-fill" :style="{width: (d.avgAccess / maxAccess * 100) + '%', background: getAccessColor(d.avgAccess)}"></div>
              </div>
              <div class="rank-value">{{ d.avgAccess.toFixed(4) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import nursingHomesData from './data/nursingHomes.json'
import streetData from './data/streets.json'

export default {
  name: 'App',
  data() {
    return {
      map: null,
      showNursingHomes: true,
      showAccessibility: true,
      nursingHomes: nursingHomesData,
      streetData: streetData,
      markers: [],
      circles: [],
      selectedDistrict: null
    }
  },
  computed: {
    totalElderly65() {
      return this.streetData.reduce((sum, s) => sum + s.elderly65, 0)
    },
    districtRank() {
      const districts = {}
      this.streetData.forEach(s => {
        if (!districts[s.district]) {
          districts[s.district] = { name: s.district, elderly: 0, accessSum: 0, count: 0 }
        }
        districts[s.district].elderly += s.elderly65
        districts[s.district].accessSum += s.accessibility
        districts[s.district].count++
      })
      return Object.values(districts)
        .map(d => ({ ...d, avgAccess: d.accessSum / d.count }))
        .sort((a, b) => b.avgAccess - a.avgAccess)
    },
    maxAccess() {
      return Math.max(...this.districtRank.map(d => d.avgAccess))
    }
  },
  mounted() {
    this.initMap()
  },
  methods: {
    initMap() {
      this.map = new AMap.Map('mapContainer', {
        zoom: 11,
        center: [114.48, 38.04],
        viewMode: '2D',
        mapStyle: 'amap://styles/light'
      })
      this.addNursingHomeMarkers()
      this.addAccessibilityCircles()
    },
    addNursingHomeMarkers() {
      this.nursingHomes.forEach(home => {
        const marker = new AMap.Marker({
          position: [home.lng, home.lat],
          title: home.name,
          icon: new AMap.Icon({
            size: new AMap.Size(28, 36),
            image: '//a.amap.com/jsapi_demos/static/demo-center/icons/poi-marker-red.png',
            imageSize: new AMap.Size(28, 36)
          })
        })
        const content = `
          <div style="padding: 12px; min-width: 260px;">
            <h4 style="margin: 0 0 12px 0; color: #1890ff; font-size: 16px;">${home.name}</h4>
            <div style="margin: 8px 0;"><span style="color: #999; margin-right: 8px;">地址:</span>${home.address}</div>
            <div style="margin: 8px 0;"><span style="color: #999; margin-right: 8px;">电话:</span>${home.phone || '暂无'}</div>
            <div style="margin: 8px 0;"><span style="color: #999; margin-right: 8px;">床位数:</span><span style="background: #52c41a; color: white; padding: 2px 8px; border-radius: 10px; font-size: 12px;">${home.beds}</span></div>
            <div style="margin: 8px 0;"><span style="color: #999; margin-right: 8px;">类型:</span>${home.type}</div>
          </div>
        `
        marker.content = content
        marker.on('click', (e) => {
          const infoWindow = new AMap.InfoWindow({ content: e.target.content, offset: new AMap.Pixel(0, -30) })
          infoWindow.open(this.map, e.target.getPosition())
        })
        this.markers.push(marker)
        this.map.add(marker)
      })
    },
    addAccessibilityCircles() {
      this.streetData.forEach(street => {
        const radius = Math.max(150, Math.min(1200, street.accessibility * 40000))
        const color = this.getAccessColor(street.accessibility)
        const circle = new AMap.Circle({
          center: [street.lng, street.lat],
          radius: radius,
          fillColor: color,
          fillOpacity: 0.35,
          strokeColor: color,
          strokeWeight: 1.5
        })
        const badge = street.accessibility > 0.05 ? '高' : street.accessibility > 0.02 ? '中' : '低'
        const badgeColor = street.accessibility > 0.05 ? '#52c41a' : street.accessibility > 0.02 ? '#faad14' : '#f5222d'
        const content = `
          <div style="padding: 12px; min-width: 260px;">
            <h4 style="margin: 0 0 12px 0; color: #1890ff; font-size: 16px;">${street.district} · ${street.name}</h4>
            <div style="margin: 6px 0;"><span style="color: #999;">类型:</span> ${street.type}</div>
            <div style="margin: 6px 0;"><span style="color: #999;">总人口:</span> ${street.totalPop.toLocaleString()}</div>
            <div style="margin: 6px 0;"><span style="color: #999;">65岁以上:</span> ${street.elderly65.toLocaleString()} 人</div>
            <div style="margin: 6px 0;"><span style="color: #999;">可达性:</span> <span style="background: ${badgeColor}; color: white; padding: 2px 8px; border-radius: 4px; font-size: 12px;">${badge} ${street.accessibility.toFixed(4)}</span></div>
            <div style="margin: 6px 0;"><span style="color: #999;">老龄化率:</span> ${street.agingRate}%</div>
          </div>
        `
        circle.content = content
        circle.on('click', (e) => {
          const infoWindow = new AMap.InfoWindow({ content: e.target.content, offset: new AMap.Pixel(0, 0) })
          infoWindow.open(this.map, e.target.getCenter())
        })
        this.circles.push(circle)
        if (this.showAccessibility) this.map.add(circle)
      })
    },
    getAccessColor(value) {
      if (value > 0.05) return '#52c41a'
      if (value > 0.02) return '#faad14'
      return '#f5222d'
    },
    focusOnDistrict(districtName) {
      const streets = this.streetData.filter(s => s.district === districtName)
      if (streets.length > 0) {
        const center = streets.reduce((sum, s) => ({ lng: sum.lng + s.lng, lat: sum.lat + s.lat }), { lng: 0, lat: 0 })
        this.map.setCenter([center.lng / streets.length, center.lat / streets.length])
        this.map.setZoom(12)
      }
    }
  },
  watch: {
    showNursingHomes(val) {
      this.markers.forEach(m => val ? this.map.add(m) : this.map.remove(m))
    },
    showAccessibility(val) {
      this.circles.forEach(c => val ? this.map.add(c) : this.map.remove(c))
    }
  }
}
</script>

<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
html, body { width: 100%; height: 100%; overflow: hidden; }
#app { width: 100%; height: 100%; }
</style>

<style scoped>
.map-wrapper {
  display: flex;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
}

#mapContainer {
  flex: 1;
  height: 100%;
}

.sidebar {
  width: 340px;
  background: linear-gradient(180deg, #f8f9fa 0%, #e9ecef 100%);
  overflow-y: auto;
  box-shadow: -2px 0 12px rgba(0,0,0,0.1);
}

.header {
  background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
  padding: 24px 20px;
  color: white;
}

.header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.header .subtitle {
  margin: 8px 0 0 0;
  font-size: 12px;
  opacity: 0.8;
}

.stats-panel {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  padding: 15px;
  background: white;
  margin: 12px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

.stat-card {
  text-align: center;
  padding: 12px 5px;
}

.stat-icon {
  font-size: 24px;
  margin-bottom: 6px;
}

.stat-value {
  font-size: 20px;
  font-weight: bold;
  color: #1890ff;
}

.stat-label {
  font-size: 11px;
  color: #999;
  margin-top: 4px;
}

.controls {
  padding: 15px;
  background: white;
  margin: 0 12px 12px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

.section-title {
  font-size: 14px;
  color: #333;
  margin: 0 0 14px 0;
  font-weight: 600;
}

.control-group {
  margin: 12px 0;
}

.toggle-switch {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.toggle-switch input {
  display: none;
}

.toggle-switch .slider {
  width: 42px;
  height: 22px;
  background: #ddd;
  border-radius: 11px;
  position: relative;
  transition: 0.3s;
  margin-right: 10px;
  flex-shrink: 0;
}

.toggle-switch .slider::after {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  background: white;
  border-radius: 50%;
  top: 2px;
  left: 2px;
  transition: 0.3s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

.toggle-switch input:checked + .slider {
  background: #1890ff;
}

.toggle-switch input:checked + .slider::after {
  left: 22px;
}

.label-text {
  font-size: 13px;
  color: #666;
}

.legend-panel {
  padding: 15px;
  background: white;
  margin: 0 12px 12px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

.legend-item {
  display: flex;
  align-items: center;
  margin: 8px 0;
}

.legend-color {
  width: 20px;
  height: 20px;
  border-radius: 5px;
  margin-right: 10px;
  flex-shrink: 0;
}

.legend-text {
  font-size: 12px;
  color: #666;
}

.district-rank {
  padding: 15px;
  background: white;
  margin: 0 12px 12px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

.rank-list {
  max-height: 420px;
  overflow-y: auto;
}

.rank-item {
  display: flex;
  align-items: center;
  padding: 10px;
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.2s;
}

.rank-item:hover {
  background: #f0f7ff;
}

.rank-index {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: bold;
  color: #999;
  background: #f5f5f5;
  margin-right: 12px;
  flex-shrink: 0;
}

.rank-index.top3 {
  background: linear-gradient(135deg, #faad14 0%, #f5222d 100%);
  color: white;
}

.rank-content {
  flex: 1;
  min-width: 0;
}

.rank-name {
  font-size: 13px;
  color: #333;
  margin-bottom: 6px;
  font-weight: 500;
}

.rank-bar {
  height: 6px;
  background: #f0f0f0;
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 4px;
}

.rank-bar-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.5s;
}

.rank-value {
  font-size: 11px;
  color: #999;
}
</style>
